/** Escape plain text for HTML fallbacks built from legacy paragraph / list fields. */
function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function paragraphsToHtml(paragraphs) {
  if (!paragraphs?.length) return "";
  return paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join("");
}

function requirementsListToHtml(items) {
  if (!items?.length) return "";
  return `<ul>${items.map((i) => `<li>${escapeHtml(i)}</li>`).join("")}</ul>`;
}

export function getJobResponsibilitiesHtml(job) {
  if (!job) return "";
  if (job.responsibilities?.trim()) return job.responsibilities;
  return paragraphsToHtml(job.aboutParagraphs);
}

export function getJobRequirementsHtml(job) {
  if (!job) return "";
  if (job.requirements?.trim()) return job.requirements;
  return requirementsListToHtml(job.primaryRequirements);
}

/** Supported salary currencies on job create / cards (ISO 4217). */
export const SALARY_CURRENCY_OPTIONS = [
  { value: "USD", symbol: "$", name: "US Dollar" },
  { value: "GBP", symbol: "£", name: "Pound Sterling" },
  { value: "EUR", symbol: "€", name: "Euro" },
  { value: "AUD", symbol: "A$", name: "Australian Dollar" },
  { value: "CAD", symbol: "C$", name: "Canadian Dollar" },
];

export function getSalaryCurrencySymbol(isoCode) {
  const c = String(isoCode || "GBP")
    .trim()
    .toUpperCase();
  const row = SALARY_CURRENCY_OPTIONS.find((o) => o.value === c);
  return row?.symbol ?? c;
}

const LOCALE_BY_CURRENCY = { GBP: "en-GB", USD: "en-US", EUR: "de-DE", AUD: "en-AU", CAD: "en-CA" };

/** e.g. `GBP £80,000 - £100,000` or `USD $120,000 - $150,000`. */
export function formatSalaryIsoAndAmounts(isoCode, min, max) {
  const currency = String(isoCode || "GBP")
    .trim()
    .toUpperCase();
  const locale = LOCALE_BY_CURRENCY[currency] || "en-GB";
  const symbol = getSalaryCurrencySymbol(currency);
  const nMin = Number(min);
  const nMax = Number(max);
  if (Number.isNaN(nMin) || Number.isNaN(nMax)) {
    return `${currency} ${symbol}${min ?? ""} - ${symbol}${max ?? ""}`;
  }
  return `${currency} ${symbol}${nMin.toLocaleString(locale)} - ${symbol}${nMax.toLocaleString(locale)}`;
}

export function getJobCardSalaryLabel(job) {
  const min = job?.salaryMin;
  const max = job?.salaryMax;
  const currency = String(job?.salaryCurrency || "GBP")
    .trim()
    .toUpperCase();
  if (min !== undefined && min !== null && String(min).trim() !== "" && max !== undefined && max !== null && String(max).trim() !== "") {
    const nMin = Number(min);
    const nMax = Number(max);
    if (!Number.isNaN(nMin) && !Number.isNaN(nMax)) {
      return formatSalaryIsoAndAmounts(currency, min, max);
    }
  }
  return job?.budgetRange || "";
}

export function getJobSubtitleLine(job) {
  if (job?.summary?.trim()) return job.summary.trim();
  return job?.summaryLine || "";
}

export function formatJobApplicationDeadline(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Canonical `seniorityLevel` values (create form, APIs). */
export const SENIORITY_LEVEL_VALUES = [
  "intern",
  "junior-or-entry",
  "mid-level-or-individual-contributor",
  "senior-or-senior-contributor",
  "lead-or-principal",
  "manager-or-people-manager",
  "senior-manager",
  "director-or-head-of",
  "senior-director",
  "executive-or-vp",
  "c-level-or-founder",
];

const SENIORITY_LABELS = {
  intern: "Intern",
  "junior-or-entry": "Junior or Entry",
  "mid-level-or-individual-contributor": "Mid-level or Individual Contributor",
  "senior-or-senior-contributor": "Senior or Senior Contributor",
  "lead-or-principal": "Lead or Principal",
  "manager-or-people-manager": "Manager or People Manager",
  "senior-manager": "Senior Manager",
  "director-or-head-of": "Director or Head of",
  "senior-director": "Senior Director",
  "executive-or-vp": "Executive or VP",
  "c-level-or-founder": "C-level or Founder",
  // legacy slugs (older listings / drafts)
  junior: "Junior or Entry",
  "mid-level": "Mid-level or Individual Contributor",
  senior: "Senior or Senior Contributor",
  lead: "Lead or Principal",
  manager: "Manager or People Manager",
  director: "Director or Head of",
  executive: "Executive or VP",
};

/** Human-readable seniority for pills and summaries. */
export function formatSeniorityDisplay(value) {
  if (!value) return "";
  return SENIORITY_LABELS[value] || value;
}

export function formatEmploymentDisplay(value) {
  const labels = {
    "full-time": "Full-time",
    "part-time": "Part-time",
    contract: "Contract (e.g. Personal, B2B)",
    internship: "Internship",
  };
  return labels[value] || value || "";
}

/** Calendar date from publish time (fallback: created). */
export function formatJobPublishedDateDisplay(job) {
  const raw = job?.publishedAt ?? job?.published_at ?? job?.createdAt ?? job?.created_at;
  if (raw == null || raw === "") return "";
  const d = raw instanceof Date ? raw : new Date(raw);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const WORK_MODE_LABELS = {
  remote: "Remote",
  hybrid: "Hybrid",
  onsite: "Onsite",
};

export function formatWorkModeDisplay(value) {
  const key = String(value || "")
    .trim()
    .toLowerCase();
  if (!key) return "";
  return WORK_MODE_LABELS[key] || value;
}
